import axios, { AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { snapshot } from "valtio";
import router from "next/router";

import {
  AccountsClient,
  AdminClient,
  AuthClient,
  FilesClient,
  ProjectsClient,
  ListingsClient,
  DealsClient,
  RfbClient,
  ClientsClient,
  BuilderUserClient,
  BuildersClient,
  RolesClient,
  UserConsentCommunicationClient,
  ListingOfferingsClient,
  NotificationsClient,
  UserLeadsClient,
  SharedOpportunitiesClient,
  UsersClient,
  QrCodesClient,
  UserVerificationClient,
  SchedulingOpenHousesClient,
  SchedulingAvailabilityClient,
  SchedulingClient,
  SchedulingAppointmentsClient,
} from "common/middleware/api.services";
import { Logger } from "common/middleware/logger";
import store from "common/middleware/store";
import { refreshUserAccessToken } from "common/middleware/api.auth";
import { getAuthRedirectUrl } from "common/utils/utils.auth";

// Shared in-flight refresh so concurrent requests only trigger one refresh call.
let refreshTokenPromise: Promise<string | undefined> | null = null;
const REFRESH_SKEW_MS = 30_000; // refresh 30s before expiry to avoid edge races

class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthError";
  }
}

function applyBearerAuthorization(
  config: Pick<InternalAxiosRequestConfig, "headers">,
  token: string
): void {
  const headers = AxiosHeaders.from(config.headers ?? {});
  headers.set("Authorization", `Bearer ${token}`);
  config.headers = headers;
}

export class ApiClientFactory {
  static readonly baseUrl = process.env.NEXT_PUBLIC_OWNSPACE_API_URL;

  static async getAuthClient(addAccessToken: boolean): Promise<AuthClient> {
    // Some endpoints require access tokens whereas some don't like .auth but .login does require one
    const axios = await this.createConfiguredAxios(addAccessToken);
    return new AuthClient(this.baseUrl, axios);
  }

  static async getRfbClient(): Promise<RfbClient> {
    let client = new RfbClient(
      this.baseUrl,
      await this.createConfiguredAxios()
    );
    return client;
  }

  static async getPurchaseOffersClient(): Promise<DealsClient> {
    const axios = await this.createConfiguredAxios(true);
    let client = new DealsClient(this.baseUrl, axios);
    return client;
  }

  static async getDealsClient(): Promise<DealsClient> {
    const axios = await this.createConfiguredAxios(true);
    let client = new DealsClient(this.baseUrl, axios);
    return client;
  }

  static async getPropertiesClient(options?: {
    addAccessToken?: boolean,
  }): Promise<ListingsClient> {
    const axios = await this.createConfiguredAxios(options?.addAccessToken);
    let client = new ListingsClient(this.baseUrl, axios);
    return client;
  }

  static async getAccountsClient(
    addAccessToken: boolean = true
  ): Promise<AccountsClient> {
    if (!addAccessToken) {
      return new AccountsClient(this.baseUrl);
    }

    const axios = await this.createConfiguredAxios();
    return new AccountsClient(this.baseUrl, axios);
  }

  static async getUsersClient(): Promise<UsersClient> {
    const axios = await this.createConfiguredAxios();
    let client = new UsersClient(this.baseUrl, axios);
    return client;
  }

  static async getClientsClient(): Promise<ClientsClient> {
    const axios = await this.createConfiguredAxios();
    let client = new ClientsClient(this.baseUrl, axios);
    return client;
  }

  static async getProjectsClient(): Promise<ProjectsClient> {
    const axios = await this.createConfiguredAxios();
    let client = new ProjectsClient(this.baseUrl, axios);
    return client;
  }

  static async getFilesClient(): Promise<FilesClient> {
    let client = new FilesClient(
      this.baseUrl,
      await this.createConfiguredAxios()
    );
    return client;
  }

  static async getBuilderUserClient(): Promise<BuilderUserClient> {
    const axios = await this.createConfiguredAxios();
    const builderUser = new BuilderUserClient(this.baseUrl, axios);
    return builderUser;
  }

  static async getBuildersClient(): Promise<BuildersClient> {
    const axios = await this.createConfiguredAxios();
    const client = new BuildersClient(this.baseUrl, axios);
    return client;
  }

  static async getRolesClient(): Promise<RolesClient> {
    const axios = await this.createConfiguredAxios(true);
    return new RolesClient(this.baseUrl, axios);
  }

  static async getUserCommunicationConsentClient(): Promise<UserConsentCommunicationClient> {
    const axios = await this.createConfiguredAxios(true);
    return new UserConsentCommunicationClient(this.baseUrl, axios);
  }

  static async getPropertyOfferingsClient(): Promise<ListingOfferingsClient> {
    const axios = await this.createConfiguredAxios(true);
    return new ListingOfferingsClient(this.baseUrl, axios);
  }

  static async getUserNotificationsClient(): Promise<NotificationsClient> {
    const axios = await this.createConfiguredAxios(true);
    return new NotificationsClient(this.baseUrl, axios);
  }

  static async getUserLeadsClient(): Promise<UserLeadsClient> {
    const axios = await this.createConfiguredAxios(true);
    return new UserLeadsClient(this.baseUrl, axios);
  }

  static async getSharedOpportunitiesClient(): Promise<SharedOpportunitiesClient> {
    const axios = await this.createConfiguredAxios(true);
    return new SharedOpportunitiesClient(this.baseUrl, axios);
  }

  static async getQrCodeClient(addAccessToken = true): Promise<QrCodesClient> {
    const axios = await this.createConfiguredAxios(addAccessToken);
    return new QrCodesClient(this.baseUrl, axios);
  }

  static async getUserVerificationClient(): Promise<UserVerificationClient> {
    const axios = await this.createConfiguredAxios();
    return new UserVerificationClient(this.baseUrl, axios);
  }

  static async getAdminClient(): Promise<AdminClient> {
    const axios = await this.createConfiguredAxios(true);
    return new AdminClient(this.baseUrl, axios);
  }

  static async getOpenHouseClient(addAccessToken = true): Promise<SchedulingOpenHousesClient> {
    const axios = await this.createConfiguredAxios(addAccessToken);
    return new SchedulingOpenHousesClient(this.baseUrl, axios);
  }

  static async getAvailabilityClient(addAccessToken = true): Promise<SchedulingAvailabilityClient> {
    const axios = await this.createConfiguredAxios(addAccessToken);
    return new SchedulingAvailabilityClient(this.baseUrl, axios);
  }

  static async getSchedulingClient(addAccessToken = true): Promise<SchedulingClient> {
    const axios = await this.createConfiguredAxios(addAccessToken);
    return new SchedulingClient(this.baseUrl, axios);
  }

  static async getAppointmentsClient(addAccessToken = true): Promise<SchedulingAppointmentsClient> {
    const axios = await this.createConfiguredAxios(addAccessToken);
    return new SchedulingAppointmentsClient(this.baseUrl, axios);
  }

  private static async createConfiguredAxios(addAccessToken: boolean = true): Promise<AxiosInstance> {
    const getSnap = () => snapshot(store);
    const snap = getSnap();

    if (snap.isSigningOut) {
      throw new AuthError("Signing out, skipping request");
    }

    const instance = axios.create();

    if (addAccessToken) {
      instance.interceptors.request.use(async (config) => {
        const snap = getSnap();
        const token = snap.accessToken?.token;

        if (!token) {
          window.location.href = getAuthRedirectUrl();
          throw new AuthError("No access token found");
        }

        // Proactively refresh if the access token is expired (or about to expire).
        const tokenExpirationDateTime = snap.accessToken?.tokenExpirationDateTime;
        let tokenExpirationMs: number | undefined;

        if (tokenExpirationDateTime != null) {
          const ms = new Date(tokenExpirationDateTime).getTime();
          tokenExpirationMs = Number.isFinite(ms) ? ms : undefined;
        }

        const shouldRefresh = tokenExpirationMs != null
          ? tokenExpirationMs - Date.now() <= REFRESH_SKEW_MS
          : false;

        if (shouldRefresh) {
          if (!snap.accessToken?.refreshToken) {
            window.location.href = getAuthRedirectUrl({ refreshToken: true });
            throw new AuthError("No refresh token found, redirecting to auth app");
          }

          if (!refreshTokenPromise) {
            refreshTokenPromise = refreshUserAccessToken().finally(() => {
              refreshTokenPromise = null;
            });
          }

          const refreshedToken = await refreshTokenPromise;

          if (refreshedToken) {
            applyBearerAuthorization(config, refreshedToken);
            Logger.debug("Refreshed access token before request");
            return config;
          } else {
            window.location.href = getAuthRedirectUrl({ refreshToken: true });
            throw new AuthError("Failed to refresh access token");
          }
        }

        applyBearerAuthorization(config, token);

        return config;
      });
    }

    const interceptor = instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error?.response?.status === 401) {
          // We need to remove the interceptor or else we could enter into an infinite loop
          instance.interceptors.response.eject(interceptor);

          const snap = getSnap();

          // Only attempt refresh for requests where we attached access tokens.
          if (addAccessToken && snap.accessToken?.refreshToken) {
            if (!refreshTokenPromise) {
              refreshTokenPromise = refreshUserAccessToken().finally(() => {
                refreshTokenPromise = null;
              });
            }

            const token = await refreshTokenPromise;
            if (token) {
              applyBearerAuthorization(error.response.config, token);
              return axios(error.response.config);
            }
          }

          const redirectUrl = new URL(getAuthRedirectUrl({ refreshToken: true }));

          if (location.origin === process.env.NEXT_PUBLIC_CHECKOUT_URL) {
            if (snap.userType) {
              redirectUrl.searchParams.set("userType", snap.userType.toLowerCase());
            } else {
              window.location.href = process.env.NEXT_PUBLIC_AUTH_URL;
              throw new AuthError("No user type found, redirecting to auth app");
            }
          }

          window.location.href = redirectUrl.href;
          throw new AuthError("Failed to refresh access token, redirecting to auth app");
        }

        // User is logged in but isn't authorized to complete the request
        if (error?.response?.status === 403) {
          router.replace("/unauthorized");
          throw new AuthError("Forbidden request, redirecting to unauthorized page");
        }

        throw error;
      }
    );

    return instance;
  }
}
